import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  WalletIcon,
  BanknotesIcon,
  CreditCardIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  CurrencyYenIcon,
  ArrowLeftIcon,
  ChartBarIcon,
  MegaphoneIcon,
  AcademicCapIcon,
  UserIcon,
  UsersIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import AdBannerManagement from '../components/AdBannerManagement';
import QualificationApproval from '../components/QualificationApproval';

import { API_BASE } from '../lib/config';
const API = API_BASE;

const AdminWalletManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('overview');
  const [platformWallet, setPlatformWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [feeHistory, setFeeHistory] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [withdrawalSettings, setWithdrawalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBankForm, setShowBankForm] = useState(false);
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);
  const [showAddAdminForm, setShowAddAdminForm] = useState(false);
  const [showSettingsForm, setShowSettingsForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  // フォーム状態
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    branch_name: '',
    account_type: '普通',
    account_number: '',
    account_holder_name: ''
  });

  const [withdrawForm, setWithdrawForm] = useState({
    amount: '',
    bank_account_id: ''
  });

  const [settingsForm, setSettingsForm] = useState({
    min_withdrawal_amount: 1000,
    withdrawal_fee_type: 'fixed',
    withdrawal_fee_amount: 0,
    withdrawal_schedule: 'instant',
    auto_process: false
  });

  const [stripeSettings, setStripeSettings] = useState({
    publishable_key: '',
    secret_key: '',
    webhook_secret: '',
    environment: 'test' // 'test' or 'live'
  });

  // アカウント設定用の状態変数
  const [accountForm, setAccountForm] = useState({
    currentPassword: '',
    newEmail: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [adminList, setAdminList] = useState([]);
  const [newAdminForm, setNewAdminForm] = useState({
    email: '',
    password: '',
    name: ''
  });

  useEffect(() => {
    // 管理者認証を確認（通常のuser認証の代わりに）
    const adminToken = localStorage.getItem('admin_token');
    const adminUser = localStorage.getItem('admin_user');
    const deployMode = localStorage.getItem('deploy_admin_mode');

    if (!adminToken || !adminUser) {
      navigate('/admin-login');
      return;
    }

    // 管理者ユーザー情報をパース
    try {
      const parsedAdminUser = JSON.parse(adminUser);
      if (parsedAdminUser.role !== 'admin') {
        navigate('/admin-login');
        return;
      }

      // デプロイモードまたはデモモードの場合はデフォルトデータで開始
      if (adminToken.startsWith('demo_token_') || adminToken.startsWith('deploy_admin_token_') || deployMode === 'true') {
        console.log('デプロイ/デモモードで管理画面を表示します');

        // デフォルトデータを設定
        setPlatformWallet({
          balance: 0,
          total_fees_earned: 0,
          total_withdrawn: 0
        });
        setTransactions([]);
        setFeeHistory([]);
        setBankAccounts([]);
        setWithdrawalRequests([]);
        setLoading(false);

        if (deployMode === 'true') {
          toast.success('デプロイ環境モードで管理画面を表示しています', { duration: 3000 });
        } else {
          toast.success('デモモードで管理画面を表示しています', { duration: 3000 });
        }
      } else {
        // 通常モード：APIからデータを取得
        fetchWalletData();
      }
    } catch (error) {
      console.error('管理者ユーザー情報のパースに失敗:', error);
      navigate('/admin-login');
      return;
    }
  }, [navigate]);

  // ウォレットデータ取得
  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');

      if (!token) {
        navigate('/admin-login');
        return;
      }

      console.log('Fetching admin wallet data...');
      console.log('API URL:', `${API}/admin/platform-wallet`);
      console.log('Token exists:', !!token);

      const [walletRes, transactionsRes, feeHistoryRes, bankAccountsRes, withdrawalRes, settingsRes] = await Promise.all([
        fetch(`${API}/admin/platform-wallet`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/platform-transactions`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/platform-fee-history`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/bank-accounts`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/withdrawal-requests`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/withdrawal-settings`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      // 認証エラーのチェック
      if (walletRes.status === 401 || transactionsRes.status === 401 ||
        feeHistoryRes.status === 401 || bankAccountsRes.status === 401 ||
        withdrawalRes.status === 401 || settingsRes.status === 401) {
        console.log('Authentication error detected, switching to demo mode');
        // ログインページに戻すのではなく、デモモードで管理画面を表示
        setPlatformWallet({
          balance: 0,
          total_fees_earned: 0,
          total_withdrawn: 0
        });
        setTransactions([]);
        setFeeHistory([]);
        setBankAccounts([]);
        setWithdrawalRequests([]);
        setWithdrawalSettings({
          min_withdrawal_amount: 1000,
          withdrawal_fee_type: 'fixed',
          withdrawal_fee_amount: 0,
          withdrawal_schedule: 'instant',
          auto_process: false
        });

        toast.warning('API接続エラーのため、デモモードで管理画面を表示しています');
        setLoading(false);
        return;
      }

      if (walletRes.ok) {
        const walletData = await walletRes.json();
        setPlatformWallet(walletData);
      } else {
        console.error('Failed to fetch wallet data:', walletRes.status);
      }

      if (transactionsRes.ok) {
        const transactionsData = await transactionsRes.json();
        setTransactions(transactionsData);
      } else {
        console.error('Failed to fetch transactions data:', transactionsRes.status);
        // デフォルトの空配列を設定
        setTransactions([]);
      }

      if (feeHistoryRes.ok) {
        const feeHistoryData = await feeHistoryRes.json();
        setFeeHistory(feeHistoryData);
      } else {
        console.error('Failed to fetch fee history data:', feeHistoryRes.status);
        // デフォルトの空配列を設定
        setFeeHistory([]);
      }

      if (bankAccountsRes.ok) {
        const bankAccountsData = await bankAccountsRes.json();
        setBankAccounts(bankAccountsData);
      } else {
        console.error('Failed to fetch bank accounts data:', bankAccountsRes.status);
        // デフォルトの空配列を設定
        setBankAccounts([]);
      }

      if (withdrawalRes.ok) {
        const withdrawalData = await withdrawalRes.json();
        setWithdrawalRequests(withdrawalData);
      } else {
        console.error('Failed to fetch withdrawal requests data:', withdrawalRes.status);
        // デフォルトの空配列を設定
        setWithdrawalRequests([]);
      }

      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setWithdrawalSettings(settingsData);
        setSettingsForm({
          min_withdrawal_amount: settingsData.min_withdrawal_amount,
          withdrawal_fee_type: settingsData.withdrawal_fee_type,
          withdrawal_fee_amount: settingsData.withdrawal_fee_amount,
          withdrawal_schedule: settingsData.withdrawal_schedule,
          auto_process: settingsData.auto_process
        });
      } else {
        console.error('Failed to fetch withdrawal settings data:', settingsRes.status);
        // デフォルトの設定を使用
        const defaultSettings = {
          min_withdrawal_amount: 1000,
          withdrawal_fee_type: 'fixed',
          withdrawal_fee_amount: 0,
          withdrawal_schedule: 'instant',
          auto_process: false
        };
        setWithdrawalSettings(defaultSettings);
        setSettingsForm(defaultSettings);
      }

    } catch (error) {
      console.error('管理画面データ取得エラー:', error);

      // エラーが発生した場合、デフォルトの値を設定して管理画面を表示可能にする
      if (!platformWallet) {
        setPlatformWallet({
          balance: 0,
          total_fees_earned: 0,
          total_withdrawn: 0
        });
      }

      if (!transactions || transactions.length === 0) {
        setTransactions([]);
      }

      if (!feeHistory || feeHistory.length === 0) {
        setFeeHistory([]);
      }

      if (!bankAccounts || bankAccounts.length === 0) {
        setBankAccounts([]);
      }

      if (!withdrawalRequests || withdrawalRequests.length === 0) {
        setWithdrawalRequests([]);
      }

      // ユーザーに通知
      toast.error('管理画面データの読み込みに失敗しました。デフォルト表示で継続します。');
    } finally {
      setLoading(false);
    }
  };

  // 銀行口座登録
  const handleBankAccountSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/bank-accounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bankForm)
      });

      if (response.ok) {
        toast.success('銀行口座を登録しました');
        setShowBankForm(false);
        setBankForm({
          bank_name: '',
          branch_name: '',
          account_type: '普通',
          account_number: '',
          account_holder_name: ''
        });
        fetchWalletData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || '登録に失敗しました');
      }
    } catch (error) {
      console.error('Error creating bank account:', error);
      toast.error('ネットワークエラーが発生しました');
    }
  };

  // 出金申請
  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();

    if (!withdrawForm.amount || parseFloat(withdrawForm.amount) <= 0) {
      toast.error('正しい出金額を入力してください');
      return;
    }

    if (!withdrawForm.bank_account_id) {
      toast.error('振込先口座を選択してください');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/withdrawal-request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(withdrawForm.amount),
          bank_account_id: withdrawForm.bank_account_id
        })
      });

      if (response.ok) {
        toast.success('出金申請を送信しました');
        setShowWithdrawForm(false);
        setWithdrawForm({ amount: '', bank_account_id: withdrawForm.bank_account_id });
        fetchWalletData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || '出金申請に失敗しました');
      }
    } catch (error) {
      console.error('Error creating withdrawal request:', error);
      toast.error('ネットワークエラーが発生しました');
    }
  };

  // 出金設定更新
  const handleSettingsSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/withdrawal-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settingsForm)
      });

      if (response.ok) {
        toast.success('出金設定を更新しました');
        setShowSettingsForm(false);
        fetchWalletData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || '設定の更新に失敗しました');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('ネットワークエラーが発生しました');
    }
  };

  // Stripe設定更新
  const handleStripeSettingsSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/stripe-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(stripeSettings)
      });

      if (response.ok) {
        toast.success('Stripe設定を更新しました');
        // フロントエンドとバックエンドを再起動して新しい設定を反映
        toast.info('設定反映のため、システムを再起動しています...');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Stripe設定の更新に失敗しました');
      }
    } catch (error) {
      console.error('Error updating Stripe settings:', error);
      toast.error('ネットワークエラーが発生しました');
    }
  };

  const getTransactionInfo = (transaction) => {
    switch (transaction.transaction_type) {
      case 'fee_collection':
        return {
          icon: <CurrencyYenIcon className="w-5 h-5 text-green-600" />,
          color: 'text-green-600'
        };
      case 'admin_withdrawal':
        return {
          icon: <ArrowDownTrayIcon className="w-5 h-5 text-red-600" />,
          color: 'text-red-600'
        };
      default:
        return {
          icon: <WalletIcon className="w-5 h-5 text-gray-600" />,
          color: 'text-gray-600'
        };
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'completed':
        return {
          label: '完了',
          color: 'bg-green-100 text-green-800',
          icon: <CheckCircleIcon className="w-5 h-5 text-green-600" />
        };
      case 'processing':
        return {
          label: '処理中',
          color: 'bg-yellow-100 text-yellow-800',
          icon: <ClockIcon className="w-5 h-5 text-yellow-600" />
        };
      case 'pending':
        return {
          label: '申請中',
          color: 'bg-blue-100 text-blue-800',
          icon: <ClockIcon className="w-5 h-5 text-blue-600" />
        };
      case 'failed':
        return {
          label: '失敗',
          color: 'bg-red-100 text-red-800',
          icon: <XCircleIcon className="w-5 h-5 text-red-600" />
        };
      default:
        return {
          label: '不明',
          color: 'bg-gray-100 text-gray-800',
          icon: <ClockIcon className="w-5 h-5 text-gray-600" />
        };
    }
  };

  // アカウント設定関数
  const handleEmailChange = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const adminToken = localStorage.getItem('admin_token');

      const response = await fetch(`${API}/admin/change-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          current_password: accountForm.currentPassword,
          new_email: accountForm.newEmail
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('メールアドレスを変更しました');
        setAccountForm({ ...accountForm, currentPassword: '', newEmail: '' });
      } else {
        toast.error(data.detail || 'メールアドレスの変更に失敗しました');
      }
    } catch (error) {
      console.error('メールアドレス変更エラー:', error);
      toast.error('メールアドレスの変更に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (accountForm.newPassword !== accountForm.confirmPassword) {
      toast.error('新しいパスワードと確認用パスワードが一致しません');
      setLoading(false);
      return;
    }

    try {
      const adminToken = localStorage.getItem('admin_token');

      const response = await fetch(`${API}/admin/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          current_password: accountForm.currentPassword,
          new_password: accountForm.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('パスワードを変更しました');
        setAccountForm({ ...accountForm, currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        toast.error(data.detail || 'パスワードの変更に失敗しました');
      }
    } catch (error) {
      console.error('パスワード変更エラー:', error);
      toast.error('パスワードの変更に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 管理者一覧取得
  const fetchAdminList = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');

      const response = await fetch(`${API}/admin/list`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAdminList(data);
      } else {
        console.log('管理者一覧の取得に失敗しました');
        setAdminList([]);
      }
    } catch (error) {
      console.error('管理者一覧取得エラー:', error);
      setAdminList([]);
    }
  };

  // 新しい管理者アカウント追加
  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const adminToken = localStorage.getItem('admin_token');

      const response = await fetch(`${API}/admin/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          admin_id: newAdminForm.email,
          password: newAdminForm.password,
          name: newAdminForm.name
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('新しい管理者アカウントを追加しました');
        setNewAdminForm({ email: '', password: '', name: '' });
        await fetchAdminList();
      } else {
        toast.error(data.detail || '管理者アカウントの追加に失敗しました');
      }
    } catch (error) {
      console.error('管理者追加エラー:', error);
      toast.error('管理者アカウントの追加に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 管理者削除
  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('この管理者アカウントを削除しますか？')) {
      return;
    }

    try {
      const adminToken = localStorage.getItem('admin_token');

      const response = await fetch(`${API}/admin/delete/${adminId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        toast.success('管理者アカウントを削除しました');
        await fetchAdminList();
      } else {
        const data = await response.json();
        toast.error(data.detail || '管理者アカウントの削除に失敗しました');
      }
    } catch (error) {
      console.error('管理者削除エラー:', error);
      toast.error('管理者アカウントの削除に失敗しました');
    }
  };

  useEffect(() => {
    if (activeTab === 'admin-management') {
      fetchAdminList();
    }
  }, [activeTab]);

  // 銀行口座削除処理
  const handleDeleteBankAccount = async () => {
    // TODO: Implement bank account deletion
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">運営ウォレット管理</h1>
            </div>
            <div className="bg-gradient-to-r from-green-500 to-blue-600 text-white px-4 py-2 rounded-lg">
              <div className="text-sm">運営ウォレット残高</div>
              <div className="text-xl font-bold">
                {(platformWallet?.total_balance || 0).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm">
          {/* タブナビゲーション */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', label: '概要', icon: WalletIcon },
                { id: 'fee-history', label: '手数料履歴', icon: CurrencyYenIcon },
                { id: 'transactions', label: '取引履歴', icon: ChartBarIcon },
                { id: 'ad-management', label: '広告管理', icon: MegaphoneIcon },
                { id: 'qualification-approval', label: '保有資格承認', icon: AcademicCapIcon },
                { id: 'bank-accounts', label: '銀行口座', icon: CreditCardIcon },
                { id: 'withdrawals', label: '出金履歴', icon: ArrowDownTrayIcon },
                { id: 'withdrawal-settings', label: '出金設定', icon: CogIcon },
                { id: 'stripe-settings', label: 'Stripe設定', icon: CreditCardIcon },
                { id: 'account-settings', label: 'アカウント設定', icon: UserIcon },
                { id: 'admin-management', label: '管理者一覧', icon: UsersIcon }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <tab.icon className="w-5 h-5 mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* 概要タブ */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* 統計カード */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100">現在残高</p>
                        <p className="text-2xl font-bold">
                          {(platformWallet?.total_balance || 0).toLocaleString()}
                        </p>
                      </div>
                      <WalletIcon className="h-12 w-12 text-green-200" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100">累計手数料徴収</p>
                        <p className="text-2xl font-bold">
                          {(platformWallet?.total_fees_collected || 0).toLocaleString()}
                        </p>
                      </div>
                      <CurrencyYenIcon className="h-12 w-12 text-blue-200" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100">累計出金額</p>
                        <p className="text-2xl font-bold">
                          {(platformWallet?.total_withdrawn || 0).toLocaleString()}
                        </p>
                      </div>
                      <ArrowDownTrayIcon className="h-12 w-12 text-purple-200" />
                    </div>
                  </div>
                </div>

                {/* 出金申請ボタン */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-green-50 rounded-lg p-6 text-center">
                    <ArrowDownTrayIcon className="h-12 w-12 text-green-600 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">出金申請</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      運営ウォレットから銀行口座に出金できます
                    </p>
                    <button
                      onClick={() => setShowWithdrawForm(true)}
                      disabled={!platformWallet || platformWallet.total_balance <= 0 || bankAccounts.length === 0}
                      className="bg-green-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      出金申請
                    </button>
                    {bankAccounts.length === 0 && (
                      <p className="text-red-500 text-xs mt-2">
                        まず銀行口座を登録してください
                      </p>
                    )}
                  </div>

                  <div className="bg-blue-50 rounded-lg p-6 text-center">
                    <CreditCardIcon className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">銀行口座</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      出金先の銀行口座を管理できます
                    </p>
                    <button
                      onClick={() => setActiveTab('bank-accounts')}
                      className="bg-blue-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors"
                    >
                      口座を管理
                    </button>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-6 text-center">
                    <CogIcon className="h-12 w-12 text-purple-600 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">出金設定</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      最低出金額や手数料を設定できます
                    </p>
                    <button
                      onClick={() => setActiveTab('withdrawal-settings')}
                      className="bg-purple-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-purple-600 transition-colors"
                    >
                      設定を変更
                    </button>
                  </div>
                </div>

                {/* 最近の取引 */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">最近の取引</h3>
                  {transactions.length > 0 ? (
                    <div className="space-y-3">
                      {transactions.slice(0, 5).map((transaction) => {
                        const info = getTransactionInfo(transaction);
                        return (
                          <div key={transaction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              {info.icon}
                              <div>
                                <p className="font-medium text-gray-900">{transaction.description}</p>
                                <p className="text-sm text-gray-500">
                                  {new Date(transaction.created_at).toLocaleDateString('ja-JP')}
                                </p>
                              </div>
                            </div>
                            <span className={`font-bold ${info.color}`}>
                              {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString()}
                            </span>
                          </div>
                        );
                      })}
                      <button
                        onClick={() => setActiveTab('transactions')}
                        className="w-full text-blue-600 text-sm hover:text-blue-800 transition-colors"
                      >
                        すべての取引履歴を見る
                      </button>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">取引履歴がありません</p>
                  )}
                </div>
              </div>
            )}

            {/* 手数料履歴タブ */}
            {activeTab === 'fee-history' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">手数料履歴</h3>
                {feeHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            事業者名
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            求職者名
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            バイト料金合計
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            手数料額（20%）
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            支払日
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ステータス
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {feeHistory.map((fee) => (
                          <tr key={fee.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {fee.employer_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {fee.job_seeker_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {fee.total_payment_amount.toLocaleString()}
                              {fee.work_hours && fee.hourly_rate && (
                                <div className="text-xs text-gray-500">
                                  {fee.hourly_rate.toLocaleString()}円/時  {fee.work_hours}時間
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                              {fee.platform_fee_amount.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(fee.payment_date).toLocaleDateString('ja-JP')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${fee.status === 'completed'
                                  ? 'bg-green-100 text-green-800'
                                  : fee.status === 'processing'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                {fee.status === 'completed' ? '完了' :
                                  fee.status === 'processing' ? '処理中' : '失敗'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CurrencyYenIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">手数料履歴がありません</p>
                  </div>
                )}
              </div>
            )}

            {/* 取引履歴タブ */}
            {activeTab === 'transactions' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">取引履歴</h3>
                {transactions.length > 0 ? (
                  <div className="space-y-3">
                    {transactions.map((transaction) => {
                      const info = getTransactionInfo(transaction);
                      return (
                        <div key={transaction.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center space-x-3">
                            {info.icon}
                            <div>
                              <p className="font-medium text-gray-900">{transaction.description}</p>
                              <p className="text-sm text-gray-500">
                                {new Date(transaction.created_at).toLocaleString('ja-JP')}
                              </p>
                            </div>
                          </div>
                          <span className={`font-bold ${info.color}`}>
                            {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString()}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ChartBarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">取引履歴がありません</p>
                  </div>
                )}
              </div>
            )}

            {/* 広告管理タブ */}
            {activeTab === 'ad-management' && (
              <div>
                <AdBannerManagement />
              </div>
            )}

            {/* 保有資格承認タブ */}
            {activeTab === 'qualification-approval' && (
              <div>
                <QualificationApproval />
              </div>
            )}

            {/* 銀行口座タブ */}
            {activeTab === 'bank-accounts' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">銀行口座</h3>
                  <button
                    onClick={() => setShowBankForm(true)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    新規口座を登録
                  </button>
                </div>

                {bankAccounts.length > 0 ? (
                  <div className="space-y-3">
                    {bankAccounts.map((account) => (
                      <div key={account.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {account.bank_name} {account.branch_name}
                            </h4>
                            <p className="text-gray-600">
                              {account.account_type} {account.account_number}
                            </p>
                            <p className="text-gray-600">{account.account_holder_name}</p>
                          </div>
                          {account.is_primary && (
                            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                              メイン口座
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CreditCardIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">登録された銀行口座がありません</p>
                  </div>
                )}
              </div>
            )}

            {/* 出金履歴タブ */}
            {activeTab === 'withdrawals' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">出金履歴</h3>

                {withdrawalRequests.length > 0 ? (
                  <div className="space-y-3">
                    {withdrawalRequests.map((request) => {
                      const statusInfo = getStatusInfo(request.status);
                      return (
                        <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="font-bold text-lg">
                                  {request.amount.toLocaleString()}
                                </span>
                                <span className={`px-2 py-1 text-xs rounded-full ${statusInfo.color}`}>
                                  {statusInfo.label}
                                </span>
                              </div>
                              <p className="text-gray-600 text-sm">
                                申請日: {new Date(request.requested_at).toLocaleDateString('ja-JP')}
                              </p>
                              {request.processed_at && (
                                <p className="text-gray-600 text-sm">
                                  処理日: {new Date(request.processed_at).toLocaleDateString('ja-JP')}
                                </p>
                              )}
                            </div>
                            {statusInfo.icon}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ArrowDownTrayIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">出金履歴がありません</p>
                  </div>
                )}
              </div>
            )}

            {/* アカウント設定タブ */}
            {activeTab === 'account-settings' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">アカウント設定</h2>

                {/* メールアドレス変更 */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">メールアドレス変更</h3>
                  <form onSubmit={handleEmailChange} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        現在のパスワード
                      </label>
                      <input
                        type="password"
                        value={accountForm.currentPassword}
                        onChange={(e) => setAccountForm({ ...accountForm, currentPassword: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        新しいメールアドレス
                      </label>
                      <input
                        type="email"
                        value={accountForm.newEmail}
                        onChange={(e) => setAccountForm({ ...accountForm, newEmail: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      メールアドレスを変更
                    </button>
                  </form>
                </div>

                {/* パスワード変更 */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">パスワード変更</h3>
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        現在のパスワード
                      </label>
                      <input
                        type="password"
                        value={accountForm.currentPassword}
                        onChange={(e) => setAccountForm({ ...accountForm, currentPassword: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        新しいパスワード
                      </label>
                      <input
                        type="password"
                        value={accountForm.newPassword}
                        onChange={(e) => setAccountForm({ ...accountForm, newPassword: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        minLength="8"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        パスワード確認
                      </label>
                      <input
                        type="password"
                        value={accountForm.confirmPassword}
                        onChange={(e) => setAccountForm({ ...accountForm, confirmPassword: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        minLength="8"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      パスワードを変更
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* 出金設定タブ */}
            {activeTab === 'withdrawal-settings' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">出金設定</h2>

                {withdrawalSettings && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">現在の設定</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">最低出金額</p>
                        <p className="font-bold text-lg">{withdrawalSettings.min_withdrawal_amount.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">手数料タイプ</p>
                        <p className="font-bold">
                          {withdrawalSettings.withdrawal_fee_type === 'fixed' ? '固定' :
                            withdrawalSettings.withdrawal_fee_type === 'percentage' ? 'パーセンテージ' : '無料'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">手数料額</p>
                        <p className="font-bold">
                          {withdrawalSettings.withdrawal_fee_type === 'fixed'
                            ? `${withdrawalSettings.withdrawal_fee_amount}`
                            : withdrawalSettings.withdrawal_fee_type === 'percentage'
                              ? `${withdrawalSettings.withdrawal_fee_amount}%`
                              : '無料'
                          }
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">処理スケジュール</p>
                        <p className="font-bold">
                          {withdrawalSettings.withdrawal_schedule === 'instant' ? '即時' :
                            withdrawalSettings.withdrawal_schedule === 'daily' ? '日次' : '週次'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">設定変更</h3>
                  <form onSubmit={handleSettingsSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          最低出金額 (円)
                        </label>
                        <input
                          type="number"
                          value={settingsForm.min_withdrawal_amount}
                          onChange={(e) => setSettingsForm({ ...settingsForm, min_withdrawal_amount: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="100"
                          step="100"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          手数料タイプ
                        </label>
                        <select
                          value={settingsForm.withdrawal_fee_type}
                          onChange={(e) => setSettingsForm({ ...settingsForm, withdrawal_fee_type: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="fixed">固定手数料</option>
                          <option value="percentage">パーセンテージ</option>
                          <option value="free">無料</option>
                        </select>
                      </div>
                    </div>

                    {settingsForm.withdrawal_fee_type !== 'free' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          手数料額 {settingsForm.withdrawal_fee_type === 'fixed' ? '(円)' : '(%)'}
                        </label>
                        <input
                          type="number"
                          value={settingsForm.withdrawal_fee_amount}
                          onChange={(e) => setSettingsForm({ ...settingsForm, withdrawal_fee_amount: parseFloat(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                          step={settingsForm.withdrawal_fee_type === 'fixed' ? "1" : "0.1"}
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          処理スケジュール
                        </label>
                        <select
                          value={settingsForm.withdrawal_schedule}
                          onChange={(e) => setSettingsForm({ ...settingsForm, withdrawal_schedule: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="instant">即時処理</option>
                          <option value="daily">日次処理</option>
                          <option value="weekly">週次処理</option>
                        </select>
                      </div>

                      <div className="flex items-center pt-6">
                        <input
                          type="checkbox"
                          id="auto_process"
                          checked={settingsForm.auto_process}
                          onChange={(e) => setSettingsForm({ ...settingsForm, auto_process: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="auto_process" className="ml-2 block text-sm text-gray-700">
                          自動処理を有効にする
                        </label>
                      </div>
                    </div>

                    <div className="flex space-x-4">
                      <button
                        type="submit"
                        className="bg-blue-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors"
                      >
                        設定を保存
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTab('overview')}
                        className="bg-gray-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-gray-600 transition-colors"
                      >
                        キャンセル
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Stripe設定タブ */}
            {activeTab === 'stripe-settings' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">Stripe設定</h2>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        セキュリティ注意事項
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p> Secret Key は絶対に第三者と共有しないでください</p>
                        <p> Test環境とLive環境のキーを間違えないよう注意してください</p>
                        <p> 設定変更後はシステムの再起動が必要です</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 現在の設定表示 */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">現在の設定</h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <p className="text-gray-600">環境</p>
                        <p className="font-bold text-lg">
                          {process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.startsWith('pk_live_') ? (
                            <span className="text-green-600">本番環境 (Live)</span>
                          ) : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.startsWith('pk_test_') ? (
                            <span className="text-blue-600">テスト環境 (Test)</span>
                          ) : (
                            <span className="text-red-600">未設定</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Publishable Key</p>
                        <p className="font-mono text-xs">
                          {process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?
                            `${process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY.substring(0, 12)}***${process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY.slice(-4)}` :
                            '未設定'
                          }
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Secret Key</p>
                        <p className="font-mono text-xs">sk_****_****************</p>
                        <p className="text-xs text-gray-500 mt-1">セキュリティのため非表示</p>
                      </div>
                    </div>
                  </div>

                  {/* 設定変更フォーム */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">新しい設定</h3>
                    <form onSubmit={handleStripeSettingsSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          環境選択 *
                        </label>
                        <select
                          value={stripeSettings.environment}
                          onChange={(e) => setStripeSettings({ ...stripeSettings, environment: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="test">テスト環境 (Test)</option>
                          <option value="live">本番環境 (Live)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Publishable Key *
                        </label>
                        <input
                          type="text"
                          value={stripeSettings.publishable_key}
                          onChange={(e) => setStripeSettings({ ...stripeSettings, publishable_key: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                          placeholder={stripeSettings.environment === 'live' ? 'pk_live_...' : 'pk_test_...'}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Secret Key *
                        </label>
                        <input
                          type="password"
                          value={stripeSettings.secret_key}
                          onChange={(e) => setStripeSettings({ ...stripeSettings, secret_key: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                          placeholder={stripeSettings.environment === 'live' ? 'sk_live_...' : 'sk_test_...'}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Webhook Secret (オプション)
                        </label>
                        <input
                          type="text"
                          value={stripeSettings.webhook_secret}
                          onChange={(e) => setStripeSettings({ ...stripeSettings, webhook_secret: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                          placeholder="whsec_..."
                        />
                      </div>

                      <div className="flex space-x-4">
                        <button
                          type="submit"
                          className="bg-blue-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors"
                        >
                          設定を更新
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveTab('overview')}
                          className="bg-gray-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-gray-600 transition-colors"
                        >
                          キャンセル
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Stripe連携テスト */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Stripe連携テスト</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button className="bg-green-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-600 transition-colors">
                      接続テスト
                    </button>
                    <button className="bg-blue-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors">
                      アカウント情報取得
                    </button>
                    <button className="bg-purple-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-600 transition-colors">
                      テスト決済実行
                    </button>
                  </div>
                </div>

                {/* Stripe設定ガイド */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4">Stripe設定ガイド</h3>
                  <div className="space-y-3 text-sm text-blue-800">
                    <div>
                      <strong>1. Stripeダッシュボードにアクセス</strong>
                      <p>https://dashboard.stripe.com/ にログイン</p>
                    </div>
                    <div>
                      <strong>2. APIキーを取得</strong>
                      <p>「開発者」「APIキー」から Publishable key と Secret key をコピー</p>
                    </div>
                    <div>
                      <strong>3. 本番環境への切り替え</strong>
                      <p>左上の「テストデータを表示」を「本番データを表示」に切り替え</p>
                    </div>
                    <div>
                      <strong>4. Webhook設定（推奨）</strong>
                      <p>「開発者」「Webhook」で決済完了通知を設定</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 管理者一覧タブ */}
            {activeTab === 'admin-management' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">管理者一覧</h2>
                  <button
                    onClick={() => setShowAddAdminForm(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>管理者追加</span>
                  </button>
                </div>

                {/* 管理者一覧テーブル */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          管理者情報
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          作成日時
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          操作
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {adminList.map((admin) => (
                        <tr key={admin.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <UserIcon className="w-8 h-8 text-gray-400 mr-3" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {admin.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {admin.admin_id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(admin.created_at).toLocaleDateString('ja-JP')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {admin.role !== 'super_admin' && (
                              <button
                                onClick={() => handleDeleteAdmin(admin.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                削除
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* 管理者追加フォーム */}
          {showAddAdminForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">新しい管理者を追加</h3>
                  <button
                    onClick={() => setShowAddAdminForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleAddAdmin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      管理者名
                    </label>
                    <input
                      type="text"
                      value={newAdminForm.name}
                      onChange={(e) => setNewAdminForm({ ...newAdminForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      メールアドレス
                    </label>
                    <input
                      type="email"
                      value={newAdminForm.email}
                      onChange={(e) => setNewAdminForm({ ...newAdminForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      初期パスワード
                    </label>
                    <input
                      type="password"
                      value={newAdminForm.password}
                      onChange={(e) => setNewAdminForm({ ...newAdminForm, password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      minLength="8"
                      required
                    />
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      追加
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddAdminForm(false);
                        setNewAdminForm({ email: '', password: '', name: '' });
                      }}
                      className="flex-1 bg-gray-500 text-white py-2 rounded-lg font-medium hover:bg-gray-600"
                    >
                      キャンセル
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 銀行口座登録モーダル */}
      {showBankForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-semibold mb-4">運営用銀行口座を登録</h2>
            <form onSubmit={handleBankAccountSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  銀行名 *
                </label>
                <input
                  type="text"
                  value={bankForm.bank_name}
                  onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例: 三菱UFJ銀行"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  支店名 *
                </label>
                <input
                  type="text"
                  value={bankForm.branch_name}
                  onChange={(e) => setBankForm({ ...bankForm, branch_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例: 本店"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  口座種別 *
                </label>
                <select
                  value={bankForm.account_type}
                  onChange={(e) => setBankForm({ ...bankForm, account_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="普通">普通</option>
                  <option value="当座">当座</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  口座番号 *
                </label>
                <input
                  type="text"
                  value={bankForm.account_number}
                  onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例: 1234567"
                  pattern="[0-9]{7}"
                  maxLength="7"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  口座名義人 *
                </label>
                <input
                  type="text"
                  value={bankForm.account_holder_name}
                  onChange={(e) => setBankForm({ ...bankForm, account_holder_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例: ウンエイガイシャ"
                  required
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-500 text-white py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors"
                >
                  登録
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowBankForm(false);
                    setBankForm({
                      bank_name: '',
                      branch_name: '',
                      account_type: '普通',
                      account_number: '',
                      account_holder_name: ''
                    });
                  }}
                  className="flex-1 bg-gray-500 text-white py-2 rounded-lg font-medium hover:bg-gray-600 transition-colors"
                >
                  キャンセル
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 出金申請フォーム（既存）*/}
      {showWithdrawForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-semibold mb-4">出金申請</h2>
            <form onSubmit={handleWithdrawalSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  出金金額 *
                </label>
                <input
                  type="number"
                  value={withdrawForm.amount}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="10000"
                  min="1"
                  max={platformWallet ? platformWallet.total_balance : 0}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  利用可能残高: {(platformWallet?.total_balance || 0).toLocaleString()}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  振込先口座 *
                </label>
                <select
                  value={withdrawForm.bank_account_id}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, bank_account_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">口座を選択してください</option>
                  {bankAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.bank_name} {account.branch_name} ({account.account_number})
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>注意事項:</strong><br />
                  出金申請は即日処理されます<br />
                  申請後のキャンセルはできません<br />
                  手数料はかかりません
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-green-500 text-white py-2 rounded-lg font-medium hover:bg-green-600 transition-colors"
                >
                  申請する
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowWithdrawForm(false);
                    setWithdrawForm({ amount: '', bank_account_id: withdrawForm.bank_account_id });
                  }}
                  className="flex-1 bg-gray-500 text-white py-2 rounded-lg font-medium hover:bg-gray-600 transition-colors"
                >
                  キャンセル
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWalletManagement;